import React, { useState, useEffect, useCallback } from "react";
import {
    Table,
    TableHead,
    TableRow,
    TableBody,
    TableContainer,
    TablePagination,
    Card,
    CardContent,
    Typography,
    Box,
    CircularProgress,
    IconButton,
    Tooltip,
    Modal,
    MenuItem,
    Popover,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { styled } from "@mui/system";
import axios from "axios";
import { API_URL } from "../../../../Config/api";
import PackageAdminJobClosure from "./PackageAdminJobClosure";
import PackageAdminCancellation from "./PackageAdminCancellation";

const OngoingServiceCard = styled(Card)({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: "10px",
    backgroundColor: "white",
    boxShadow: "4px 4px 10px 7px rgba(135, 135, 135, 0.05)",
    height: "55px",
    borderRadius: "10px",
    transition: "0.5s ease-in-out",
    "&:hover": {
        backgroundColor: "#F7F7F7",
        cursor: "pointer",
    },
});

const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "320px",
    bgcolor: "background.paper",
    borderRadius: "12px",
    boxShadow: 24,
    p: 2,
};

const componentModalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "90%",
    maxWidth: "1000px",
    maxHeight: "100vh",
    bgcolor: "background.paper",
    borderRadius: "12px",
    boxShadow: 24,
    overflowY: "auto",
    p: 2,
};

const PackageRevalidation = ({
    formatDate,
    auth,
    filterType,
    searchValue,
    startDateTime,
}) => {
    const accessToken = localStorage.getItem("token");
    const [activeRowData, setActiveRowData] = useState(null);
    const [packageData, setPackageData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);

    const [actionAnchorEl, setActionAnchorEl] = useState(null);
    const [closureModalOpen, setClosureModalOpen] = useState(false);
    const [cancellationModalOpen, setCancellationModalOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleActionClick = (event, row) => {
        setSelectedRow(row);
        setActionAnchorEl(event.currentTarget);
        setActiveRowData(row);
    };

    const handleActionModalClose = () => {
        setActionAnchorEl(null);
    };

    const handleClosureRevalidation = () => {
        setActionAnchorEl(null);
        setClosureModalOpen(true);
    };

    const handlePackageCancellation = () => {
        setActionAnchorEl(null);
        setCancellationModalOpen(true);
    };

    const handleClosureModalClose = () => {
        setClosureModalOpen(false);
    };

    const handleCancellationModalClose = () => {
        setCancellationModalOpen(false);
    };

    const fetchPackages = useCallback(async () => {
        setLoading(true);

        try {
            const queryParams = {};

            if (filterType === "eventCode" && searchValue) {
                queryParams.eve_code = searchValue;
            } else if (filterType === "ptnPhn" && searchValue) {
                queryParams.patient_no = searchValue;
            } else if (filterType === "ptnName" && searchValue) {
                queryParams.patient_name = searchValue;
            } else if (filterType === "clrPhn" && searchValue) {
                queryParams.caller_no = searchValue;
            } else if (filterType === "profName" && searchValue) {
                queryParams.prof_name = searchValue;
            }

            if (startDateTime) {
                queryParams.date = startDateTime;
            }

            const response = await axios.get(
                `${API_URL}/packages/Ongoing_Event/`,
                {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                        "Content-Type": "application/json",
                    },
                    params: queryParams,
                }
            );

            if (
                response.data?.msg === "data not foundf" ||
                response.data?.msg === "data not found"
            ) {
                setPackageData([]);
            } else {
                setPackageData(response.data || []);
            }

            setPage(0);
        } catch (error) {
            setPackageData([]);
        } finally {
            setLoading(false);
        }
    }, [accessToken, filterType, searchValue, startDateTime]);

    useEffect(() => {
        fetchPackages();
    }, [fetchPackages]);

    return (
        <>
            <TableContainer
                sx={{
                    height:
                        packageData.length === 0 || packageData.length < 5
                            ? "60vh"
                            : "auto",
                    overflowY: "hidden !important",
                    mt: 1,
                }}
            >
                <Table sx={{ minWidth: 1100 }}>
                    <TableHead>
                        <TableRow>
                            <OngoingServiceCard
                                sx={{
                                    background: "rgb(173, 219, 177)",
                                    color: "#423737",
                                    borderRadius: "8px 10px 0 0",
                                    height: "50px",
                                    "&:hover": {
                                        background: "rgb(173, 219, 177)",
                                        cursor: "pointer"
                                    }
                                }}
                            >
                                <CardContent
                                    sx={{
                                        flex: 1.5,
                                        borderRight: "1px solid #fff",
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: "13px",
                                            fontWeight: 600,
                                        }}
                                    >
                                        Event Code
                                    </Typography>
                                </CardContent>

                                <CardContent
                                    sx={{
                                        flex: 1.5,
                                        borderRight: "1px solid #fff",
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: "13px",
                                            fontWeight: 600,
                                        }}
                                    >
                                        Patient Name
                                    </Typography>
                                </CardContent>

                                <CardContent
                                    sx={{
                                        flex: 1.3,
                                        borderRight: "1px solid #fff",
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: "13px",
                                            fontWeight: 600,
                                        }}
                                    >
                                        Caller No
                                    </Typography>
                                </CardContent>

                                <CardContent
                                    sx={{
                                        flex: 1.5,
                                        borderRight: "1px solid #fff",
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: "13px",
                                            fontWeight: 600,
                                        }}
                                    >
                                        Package Name
                                    </Typography>
                                </CardContent>

                                <CardContent
                                    sx={{
                                        flex: 1.2,
                                        borderRight: "1px solid #fff",
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: "13px",
                                            fontWeight: 600,
                                        }}
                                    >
                                        Start Date
                                    </Typography>
                                </CardContent>

                                <CardContent
                                    sx={{
                                        flex: 1.2,
                                        borderRight: "1px solid #fff",
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: "13px",
                                            fontWeight: 600,
                                        }}
                                    >
                                        End Date
                                    </Typography>
                                </CardContent>

                                <CardContent
                                    sx={{
                                        flex: 1.5,
                                        borderRight: "1px solid #fff",
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: "13px",
                                            fontWeight: 600,
                                        }}
                                    >
                                        Pay Status
                                    </Typography>
                                </CardContent>

                                <CardContent
                                    sx={{
                                        flex: 0.5,
                                        borderRight: "1px solid #fff",
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: "13px",
                                            fontWeight: 600,
                                        }}
                                    >
                                        Job Closure
                                    </Typography>
                                </CardContent>

                                <CardContent
                                    sx={{
                                        flex: 1,
                                        borderRight: "1px solid #fff",
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: "13px",
                                            fontWeight: 600,
                                        }}
                                    >
                                        Added By
                                    </Typography>
                                </CardContent>

                                <CardContent sx={{ flex: 0.5 }}>
                                    <Typography
                                        sx={{
                                            fontSize: "13px",
                                            fontWeight: 600,
                                        }}
                                    >
                                        Action
                                    </Typography>
                                </CardContent>
                            </OngoingServiceCard>
                        </TableRow>
                    </TableHead>

                    {loading ? (
                        <Box
                            sx={{
                                display: "flex",
                                mt: 10,
                                justifyContent: "center",
                                height: "130px",
                            }}
                        >
                            <CircularProgress />
                        </Box>
                    ) : (
                        <TableBody>
                            {packageData.length === 0 ? (
                                <TableRow>
                                    <CardContent>
                                        <Typography
                                            variant="body2"
                                            sx={{ p: 2 }}
                                        >
                                            {searchValue
                                                ? `No packages found for ${searchValue}`
                                                : "No Package Data Available"}
                                        </Typography>
                                    </CardContent>
                                </TableRow>
                            ) : (
                                packageData
                                    .slice(
                                        page * rowsPerPage,
                                        page * rowsPerPage + rowsPerPage
                                    )
                                    .map((row, index) => (
                                        <TableRow key={index}>
                                            <OngoingServiceCard
                                                sx={{
                                                    height: "3.5rem",
                                                    background: "#f1f1f1",
                                                    borderLeft:
                                                        "4px solid transparent",
                                                    transition:
                                                        "all 0.3s ease-in-out",
                                                    "&:hover": {
                                                        borderLeft:
                                                            "4px solid rgb(19 166 31)",
                                                        borderRight:
                                                            "4px solid rgb(19 166 31)",
                                                    },
                                                }}
                                            >
                                                <CardContent sx={{ flex: 1.5 }}>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontSize: "13px",
                                                        }}
                                                    >
                                                        {row.event_code}
                                                    </Typography>
                                                </CardContent>

                                                <CardContent
                                                    sx={{
                                                        flex: 1.5,
                                                        overflow: "hidden",
                                                        textOverflow:
                                                            "ellipsis",
                                                        whiteSpace: "nowrap",
                                                    }}
                                                >
                                                    <Tooltip
                                                        title={
                                                            row.patient_name ||
                                                            ""
                                                        }
                                                    >
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                fontSize: "13px",
                                                            }}
                                                        >
                                                            {row.patient_name ||
                                                                "-"}
                                                        </Typography>
                                                    </Tooltip>
                                                </CardContent>

                                                <CardContent sx={{ flex: 1.3 }}>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontSize: "13px",
                                                        }}
                                                    >
                                                        {row.caller_no || "-"}
                                                    </Typography>
                                                </CardContent>

                                                <CardContent
                                                    sx={{
                                                        flex: 1.5,
                                                        overflow: "hidden",
                                                        textOverflow:
                                                            "ellipsis",
                                                        whiteSpace: "nowrap",
                                                    }}
                                                >
                                                    <Tooltip
                                                        title={
                                                            row.package_name ||
                                                            "N/A"
                                                        }
                                                    >
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                fontSize: "13px",
                                                                fontWeight: 500,
                                                            }}
                                                        >
                                                            {row.package_name ||
                                                                "-"}
                                                        </Typography>
                                                    </Tooltip>
                                                </CardContent>

                                                <CardContent sx={{ flex: 1.2 }}>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontSize: "13px",
                                                        }}
                                                    >
                                                        {row.startDate
                                                            ? formatDate
                                                                ? formatDate(
                                                                    row.startDate
                                                                )
                                                                : row.startDate
                                                            : "-"}
                                                    </Typography>
                                                </CardContent>

                                                <CardContent sx={{ flex: 1.2 }}>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontSize: "13px",
                                                        }}
                                                    >
                                                        {row.endDate
                                                            ? formatDate
                                                                ? formatDate(
                                                                    row.endDate
                                                                )
                                                                : row.endDate
                                                            : "-"}
                                                    </Typography>
                                                </CardContent>

                                                <CardContent sx={{ flex: 1.5 }}>
                                                    {row.discount_type_name?.toLowerCase() ===
                                                        "complimentary" ? (
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                alignItems:
                                                                    "center",
                                                                gap: 0.5,
                                                                color: "success.main",
                                                                fontSize: "13px",
                                                                fontWeight: 600,
                                                            }}
                                                        >
                                                            <CheckCircleIcon
                                                                sx={{
                                                                    fontSize: 18,
                                                                }}
                                                            />
                                                            Complimentary
                                                        </Box>
                                                    ) : (
                                                        <Tooltip
                                                            title={`Total: ₹${row.total_amount ||
                                                                0
                                                                } | Pending: ₹${row.remaining_amount ||
                                                                0
                                                                }`}
                                                            arrow
                                                        >
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    fontSize:
                                                                        "13px",
                                                                    fontWeight: 600,
                                                                    color:
                                                                        row.remaining_amount >
                                                                            0
                                                                            ? "error.main"
                                                                            : "success.main",
                                                                }}
                                                            >
                                                                {row.received_by ===
                                                                    null
                                                                    ? `${row.remaining_amount ||
                                                                    0
                                                                    } / ${row.total_amount ||
                                                                    0
                                                                    }`
                                                                    : row.received_by ===
                                                                        1
                                                                        ? "Received By Professional"
                                                                        : row.received_by ===
                                                                            2
                                                                            ? "Received To Spero"
                                                                            : row.received_by ===
                                                                                3
                                                                                ? "Received By Desk"
                                                                                : "0"}
                                                            </Typography>
                                                        </Tooltip>
                                                    )}
                                                </CardContent>

                                                <CardContent
                                                    sx={{
                                                        flex: 0.5,
                                                        display: "flex",
                                                        alignItems: "center",
                                                    }}
                                                >
                                                    {row.closure_status ? (
                                                        <Tooltip title="Job Closure (Done)">
                                                            <CheckCircleIcon
                                                                sx={{
                                                                    fontSize: 22,
                                                                    color: "success.main",
                                                                }}
                                                            />
                                                        </Tooltip>
                                                    ) : (
                                                        <Tooltip title="Job Closure (Pending)">
                                                            <CancelIcon
                                                                sx={{
                                                                    fontSize: 22,
                                                                    color: "error.main",
                                                                }}
                                                            />
                                                        </Tooltip>
                                                    )}
                                                </CardContent>

                                                <CardContent sx={{ flex: 1 }}>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontSize: "13px",
                                                        }}
                                                    >
                                                        {row.added_by || "-"}
                                                    </Typography>
                                                </CardContent>

                                                <CardContent
                                                    sx={{
                                                        flex: 0.5,
                                                        display: "flex",
                                                        justifyContent:
                                                            "center",
                                                        alignItems: "center",
                                                    }}
                                                >
                                                    <Tooltip title="Actions">
                                                        <IconButton
                                                            size="small"
                                                            onClick={(event) => handleActionClick(event, row)}
                                                            sx={{
                                                                padding: "4px",
                                                                color: "black",
                                                            }}
                                                        >
                                                            <MoreHorizIcon
                                                                sx={{
                                                                    fontSize: 22,
                                                                }}
                                                            />
                                                        </IconButton>
                                                    </Tooltip>
                                                </CardContent>
                                            </OngoingServiceCard>
                                        </TableRow>
                                    ))
                            )}
                        </TableBody>
                    )}
                </Table>
            </TableContainer>

            <TablePagination
                sx={{ overflowY: "hidden" }}
                rowsPerPageOptions={[5, 10, 25, 100]}
                component="div"
                count={packageData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />

            <Popover
                open={Boolean(actionAnchorEl)}
                anchorEl={actionAnchorEl}
                onClose={handleActionModalClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
                PaperProps={{
                    sx: {
                        mt: 0.5,
                        borderRadius: "10px",
                        boxShadow: "0px 4px 15px rgba(0,0,0,0.15)",
                        minWidth: "200px",
                        overflow: "hidden",
                    },
                }}
            >
                <Box sx={{ p: 0.5 }}>
                    <MenuItem
                        onClick={handleClosureRevalidation}
                        sx={{
                            borderRadius: "7px",
                            fontSize: "13px",
                            minHeight: "38px",
                        }}
                    >
                        Closure Revalidation
                    </MenuItem>

                    <MenuItem
                        onClick={handlePackageCancellation}
                        sx={{
                            borderRadius: "7px",
                            fontSize: "13px",
                            minHeight: "38px",
                        }}
                    >
                        Package Cancellation
                    </MenuItem>
                </Box>
            </Popover>

            <Modal
                open={closureModalOpen}
                onClose={handleClosureModalClose}
            >
                <Box sx={componentModalStyle}>
                    {activeRowData && (
                        <PackageAdminJobClosure
                            eventID={activeRowData.eve_id}
                            dtlsId={activeRowData.dtls_id}
                            shift={activeRowData.shift}
                            accessToken={accessToken}
                            defaultStartDate={activeRowData.startDate}
                            defaultEndDate={activeRowData.endDate}
                            onClose={handleClosureModalClose}
                            onSubmitted={fetchPackages}
                        />
                    )}
                </Box>
            </Modal>

            <Modal
                open={cancellationModalOpen}
                onClose={handleCancellationModalClose}
            >
                <Box sx={componentModalStyle}>
                    <PackageAdminCancellation
                        selectedRow={selectedRow}
                        eveId={selectedRow?.eve_id}
                        onClose={handleCancellationModalClose}
                        fetchPackages={fetchPackages}
                    />
                </Box>
            </Modal>
        </>
    );
};

export default PackageRevalidation;